import { supabase } from '../supabase';

const FUNCTIONS_BASE = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;

/**
 * Create or retrieve a Stripe Connect Express account for the current user.
 * Returns an onboarding URL if the account needs setup.
 */
export async function createConnectAccount(returnUrl?: string): Promise<{
  accountId: string;
  onboardingUrl?: string;
  status: string;
}> {
  const session = await supabase.auth.getSession();
  const token = session.data.session?.access_token;

  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${FUNCTIONS_BASE}/create-connect-account`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ returnUrl })
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to create Connect account');

  return data;
}

/**
 * Check the current user's Stripe Connect account status from the local DB.
 */
export async function getConnectStatus(): Promise<{
  hasAccount: boolean;
  accountId: string | null;
  status: string | null;
}> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { hasAccount: false, accountId: null, status: null };

  const { data } = await supabase
    .from('users')
    .select('stripe_account_id, stripe_account_status')
    .eq('id', user.id)
    .single();

  return {
    hasAccount: !!data?.stripe_account_id,
    accountId: data?.stripe_account_id || null,
    status: data?.stripe_account_status || null
  };
}

/**
 * Check the current user's Stripe Connect status by querying Stripe directly.
 * Also updates the DB if the status has changed.
 * Use this when the user returns from Stripe onboarding.
 */
export async function refreshConnectStatus(): Promise<{
  hasAccount: boolean;
  accountId: string | null;
  status: string | null;
  detailsSubmitted?: boolean;
}> {
  const session = await supabase.auth.getSession();
  const token = session.data.session?.access_token;

  if (!token) return { hasAccount: false, accountId: null, status: null };

  try {
    const response = await fetch(`${FUNCTIONS_BASE}/check-connect-status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to check status');

    return {
      hasAccount: data.hasAccount ?? false,
      accountId: data.accountId || null,
      status: data.status || null,
      detailsSubmitted: data.detailsSubmitted ?? false,
    };
  } catch (err) {
    console.error('Failed to refresh connect status:', err);
    // Fall back to DB check
    return getConnectStatus();
  }
}

/**
 * Create a ticket payment for an event.
 */
export async function createTicketPayment(params: {
  eventId: string;
  tickets: Array<{ tierName: string; quantity: number; attendeeNames?: string[] }>;
  buyerName?: string;
  buyerEmail?: string;
}): Promise<{
  clientSecret: string | null;
  orderId: string;
  free?: boolean;
  breakdown: {
    subtotal: number;
    serviceFee: number;
    commissionFee: number;
    stripeFee: number;
    vatAmount: number;
    totalAmount: number;
    organizerPayout: number;
    currency: string;
  };
}> {
  const session = await supabase.auth.getSession();
  const token = session.data.session?.access_token;

  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${FUNCTIONS_BASE}/create-ticket-payment`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(params)
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to create payment');

  return data;
}

/**
 * Download PDF tickets for a completed order.
 */
export async function downloadTickets(orderId: string): Promise<void> {
  const session = await supabase.auth.getSession();
  const token = session.data.session?.access_token;

  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${FUNCTIONS_BASE}/generate-tickets`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ orderId })
  });

  if (!response.ok) {
    const text = await response.text();
    let errorMsg = 'Failed to generate tickets';
    try {
      const json = JSON.parse(text);
      errorMsg = json.error || errorMsg;
    } catch (err) {
      console.error('Failed to parse error response:', err);
    }
    throw new Error(errorMsg);
  }

  // Download the PDF
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `tickets-${orderId.slice(0, 8)}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Request event deletion. If the event has paid ticket orders,
 * all buyers are refunded via Stripe before the event is cancelled.
 */
export async function requestEventDeletion(params: {
  eventId: string;
  reason?: string;
}): Promise<{
  success: boolean;
  action: 'deleted' | 'cancelled_with_refunds' | 'pending_approval';
  message: string;
  refundSummary?: {
    totalOrders: number;
    refunded: number;
    failed: number;
    totalAmountRefunded: number;
  };
}> {
  const session = await supabase.auth.getSession();
  const token = session.data.session?.access_token;

  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${FUNCTIONS_BASE}/request-event-deletion`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(params)
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to request event deletion');

  return data;
}

/**
 * Admin: Approve or reject an event cancellation request.
 * On approve, triggers Stripe refunds for all paid orders.
 */
export async function approveEventCancellation(params: {
  requestId: string;
  action: 'approve' | 'reject';
}): Promise<{
  success: boolean;
  message: string;
  refundSummary?: {
    totalOrders: number;
    refunded: number;
    failed: number;
  };
}> {
  const session = await supabase.auth.getSession();
  const token = session.data.session?.access_token;

  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${FUNCTIONS_BASE}/approve-event-cancellation`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(params)
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Failed to process cancellation');

  return data;
}
